/**
 * XML Block Import Service
 * Handles the import of XML blocks to standalone blocks via API
 */

import { createStandaloneBlock, updateMetaTags, patchComplexBlockDirect, translateStandaloneBlock, getStandaloneBlock, sendToTesting, moveToQaComplete, publishDesign } from './services/complexDocumentsAPI';
import { extractUnsupportedCharacters } from './helper';
import { HTML_BLOCK, COMPLEX_DOCUMENT_VERSION, DESIGN_TYPE } from './complexDocumentConstants';
import {
  parseXMLBlocks,
  getBlockName,
  getAvailableLanguages,
  getLanguageContent,
} from './xmlBlockImporter';

/**
 * Create payload for standalone block
 * @param {Object} block - Block object from XML
 * @param {string} language - Language code
 * @param {number} resourceGroupId - Resource group ID
 * @param {number} categoryId - Category ID
 * @param {string} groupId - Group ID for related blocks (optional)
 * @returns {Object} Payload for createStandaloneBlock API
 */
const createBlockPayload = (block, language, resourceGroupId, categoryId, groupId = '') => {
  const blockName = getBlockName(block);
  const htmlContent = getLanguageContent(block, language);
  
  // Default HTML content if none provided
  const defaultHtml = '<p style="text-align: left"> <br/> </p>';
  const content = htmlContent || defaultHtml;
  
  // Determine block type based on category
  const blockType = block.category && block.category.toLowerCase().includes('footer')
    ? 'footer'
    : block.category.toLowerCase().includes('header')
      ? 'header'
      : 'content';
  
  return {
    categoryId: categoryId,
    resourceGroupId,
    parentBlockName: `${blockName}_${language}`,
    isReferenced: true,
    blocks: [
      {
        blockType: HTML_BLOCK,
        contents: content,
        orderNumber: 1,
        style: { height: 100, lineSpacing: 1.1, paragraphSpacing: 10, alignment: 'justify' },
        isEditable: true, // To allow default contents
        variableContract: [{ name: `paragraph-${blockName.replaceAll(" ", "-")}`, type: 'html' }],
      },
    ],
    type: blockType,
    language,
    style: {
      fontFamily: 'Arial',
      fontSize: 13,
      lineSpacing: 1.1
    },
    ...(groupId && { groupId }),
  };
};

/**
 * Publish a block through the approval lifecycle
 * @param {string} blockId - Block ID to publish
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Object>} Publishing result with status
 */
const publishBlock = async (blockId, onProgress) => {
  try {
    // Step 1: Move to QA Ready
    if (onProgress) {
      onProgress({
        publishStage: 'qa-ready',
        blockId,
        publishStatus: 'qa-ready',
      });
    }
    
    const qaReadyResponse = await sendToTesting(blockId, DESIGN_TYPE.BLOCK);
    const successStatusCodes = [2601, 728, 1904];
    
    if (!successStatusCodes.includes(qaReadyResponse?.statusCode)) {
      const errorMessage = qaReadyResponse?.errors?.[0]?.description || 'Failed to move to QA Ready';
      return {
        success: false,
        stage: 'qa-ready',
        errorMessage,
        blockId,
      };
    }

    // Step 2: Move to QA Complete
    if (onProgress) {
      onProgress({
        publishStage: 'qa-complete',
        blockId,
        publishStatus: 'qa-complete',
      });
    }
    
    const qaCompleteResponse = await moveToQaComplete(blockId, DESIGN_TYPE.BLOCK);
    if (qaCompleteResponse?.errors && qaCompleteResponse.errors.length > 0) {
      const errorMessage = qaCompleteResponse.errors[0]?.description || 'Failed to move to QA Complete';
      return {
        success: false,
        stage: 'qa-complete',
        errorMessage,
        blockId,
      };
    }

    // Step 3: Publish
    if (onProgress) {
      onProgress({
        publishStage: 'published',
        blockId,
        publishStatus: 'published',
      });
    }
    
    const publishResponse = await publishDesign(blockId, DESIGN_TYPE.BLOCK);
    if (publishResponse?.errors && publishResponse.errors.length > 0) {
      const errorMessage = publishResponse.errors[0]?.description || 'Failed to publish';
      return {
        success: false,
        stage: 'published',
        errorMessage,
        blockId,
      };
    }

    return {
      success: true,
      stage: 'published',
      blockId,
    };
  } catch (error) {
    console.error('Publishing error:', error);
    return {
      success: false,
      stage: 'error',
      errorMessage: error.message || 'Unknown publishing error',
      blockId,
    };
  }
};

/**
 * Create metadata payload for updateMetaTags
 * @param {Object} block - Block object from XML
 * @returns {Array} Array of metadata objects
 */
const createMetadataPayload = (block) => {
  const metadata = [];
  metadata.push({ key: "type", value: "paragraph" });
  
  if (block.povc && block.povc.trim() !== '') {
    metadata.push({ key: 'povc', value: block.povc.trim() });
  }
  
  if (block.iytxtbs && block.iytxtbs.trim() !== '') {
    metadata.push({ key: 'iytxtbs', value: block.iytxtbs.trim() });
  }
  
  if (block.category && block.category.trim() !== '') {
    metadata.push({ key: 'category', value: block.category.trim() });
  }

  if (block.nameFr && block.nameFr.trim() !== '') {
    metadata.push({ key: 'nameFr', value: block.nameFr });
  }

  if (block.nameNl && block.nameNl.trim() !== '') {
    metadata.push({ key: 'nameNl', value: block.nameNl });
  }
  
  return metadata;
};

/**
 * Import XML blocks to standalone blocks
 * @param {string} xmlContent - XML file content as string
 * @param {number} resourceGroupId - Resource group ID (team ID)
 * @param {Function} onProgress - Progress callback function
 * @param {number} categoryId - Category ID
 * @returns {Promise<Object>} Import result with statistics
 */
export const importXMLBlocks = async (xmlContent, resourceGroupId, onProgress, categoryId) => {
  const result = {
    totalBlocks: 0,
    processedBlocks: 0,
    successfulImports: 0,
    failedImports: 0,
    blockStatuses: [],
    errors: [],
    failedBlocks: [],
    publishedBlocks: 0,
    qaReadyBlocks: 0,
    qaCompleteBlocks: 0,
    publishErrors: 0,
  };

  const failedBlockIndices = new Set();
  
  try {
    // Parse XML
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: 'Parsing XML file...',
      });
    }
    
    const blocks = parseXMLBlocks(xmlContent);
    result.totalBlocks = blocks.length;
    
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: `Found ${blocks.length} blocks to import`,
        totalBlocks: blocks.length,
      });
    }
    
    // Process each block
    for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
      const block = blocks[blockIndex];
      const blockName = getBlockName(block);
      const availableLanguages = getAvailableLanguages(block);
      
      if (availableLanguages.length === 0) {
        // Skip blocks with no valid language content
        result.processedBlocks++;
        continue;
      }
      
      let groupId = '';
      // let firstInstanceBlockId;
      let firstLanguageSuccess = false;
      
      // Process each language for this block
      for (let langIndex = 0; langIndex < availableLanguages.length; langIndex++) {
        const language = availableLanguages[langIndex];
        const statusId = `${blockIndex}_${language}`;
        
        // Create status object
        const statusObj = {
          blockId: blockName,
          language,
          status: 'processing',
          blockName,
          blockIndex,
          publishStatus: 'not-started',
          publishStage: null,
          publishErrorMessage: null,
        };
        
        result.blockStatuses.push(statusObj);
        
        if (onProgress) {
          onProgress({
            stage: 'importing',
            currentBlock: blockName,
            currentLanguage: language,
            blockIndex: blockIndex + 1,
            totalBlocks: blocks.length,
            processedBlocks: result.processedBlocks,
            statusObj,
          });
        }
        
        try {
          // Create payload
          const payload = createBlockPayload(block, language, resourceGroupId, categoryId, groupId);
          
          // Call API
          let response;
          // if (!firstLanguageSuccess) { // Create the standalone block for the first intance
            response = await createStandaloneBlock(payload);
          /*} else { // Translate to retain group Id
            const translationResponse = await translateStandaloneBlock({ documentId: firstInstanceBlockId, languageCode: language });
            console.log("translateStandaloneBlock :: ", translationResponse);
            payload.parentBlockId = translationResponse.data?.id;
            payload.language = language;
            response = await patchComplexBlockDirect(payload, false);
            console.log({ response });
          }*/
          
          if (response.data?.id) {
            // Success
            statusObj.status = 'success';
            statusObj.blockId = response.data.id;
            result.successfulImports++;
            
            // Extract groupId from first language response
            if (!firstLanguageSuccess) {
              groupId = response.data.groupId;
              firstLanguageSuccess = true;
              
              // Update metadata for the first language only
              const metadata = createMetadataPayload(block);
              if (metadata.length > 0) {
                try {
                  await updateMetaTags('block-document', groupId, metadata);
                } catch (metaError) {
                  console.warn('Failed to update metadata:', metaError);
                  // Don't fail the import if metadata update fails
                }
              }
            }

            // Publish the standalone block
            statusObj.publishStatus = 'processing';
            if (onProgress) {
              onProgress({
                stage: 'publishing',
                currentBlock: blockName,
                currentLanguage: language,
                blockIndex: blockIndex + 1,
                totalBlocks: blocks.length,
                statusObj: { ...statusObj, publishStatus: 'processing' },
              });
            }

            const publishResult = await publishBlock(response.data.id, (publishProgress) => {
              if (onProgress) {
                onProgress({
                  stage: 'publishing',
                  currentBlock: blockName,
                  currentLanguage: language,
                  blockIndex: blockIndex + 1,
                  totalBlocks: blocks.length,
                  statusObj: {
                    ...statusObj,
                    publishStatus: publishProgress.publishStatus || statusObj.publishStatus,
                    publishStage: publishProgress.publishStage || statusObj.publishStage,
                  },
                });
              }
            });

            // Update status with publishing result
            if (publishResult.success) {
              statusObj.publishStatus = 'published';
              statusObj.publishStage = 'published';
              result.publishedBlocks++;
            } else {
              statusObj.publishStatus = 'publish-error';
              statusObj.publishStage = publishResult.stage;
              statusObj.publishErrorMessage = publishResult.errorMessage;
              result.publishErrors++;
              
              // Track publishing stage for statistics
              if (publishResult.stage === 'qa-ready') {
                // Failed at QA Ready, so not counted
              } else if (publishResult.stage === 'qa-complete') {
                result.qaReadyBlocks++;
              } else if (publishResult.stage === 'published') {
                result.qaCompleteBlocks++;
              }
              
              result.errors.push({
                block: blockName,
                language,
                error: `Publishing failed: ${publishResult.errorMessage}`,
                type: 'publishing',
              });
              failedBlockIndices.add(blockIndex);
            }
          } else {
            // Error
            statusObj.status = 'error';
            failedBlockIndices.add(blockIndex);
            const error = response?.errors?.[0] || {};
            let errorMessage = error.description || 'Unknown error';
            
            if (error.code === 5601) {
              const unsupportedChars = extractUnsupportedCharacters(errorMessage);
              errorMessage = `Unsupported characters: ${unsupportedChars}`;
            }
            
            statusObj.errorMessage = errorMessage;
            result.failedImports++;
            result.errors.push({
              block: blockName,
              language,
              error: errorMessage,
            });
          }
        } catch (error) {
          // Network or other errors
          console.log("Error in trying to create standalone block :: ", error);
          statusObj.status = 'error';
          statusObj.errorMessage = error.message || 'Failed to import block';
          result.failedImports++;
          result.errors.push({
            block: blockName,
            language,
            error: error.message || 'Unknown error',
          });
          failedBlockIndices.add(blockIndex);
        }
        
        result.processedBlocks++;
        
        if (onProgress) {
          onProgress({
            stage: statusObj.publishStatus === 'processing' || statusObj.publishStatus === 'published' || statusObj.publishStatus === 'publish-error' 
              ? 'publishing' 
              : 'importing',
            currentBlock: blockName,
            currentLanguage: language,
            blockIndex: blockIndex + 1,
            totalBlocks: blocks.length,
            processedBlocks: result.processedBlocks,
            successfulImports: result.successfulImports,
            failedImports: result.failedImports,
            publishedBlocks: result.publishedBlocks,
            publishErrors: result.publishErrors,
            statusObj,
          });
        }
      }
    }

    result.failedBlocks = blocks.filter((_, idx) => failedBlockIndices.has(idx));
    
    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: 'Import and publishing completed',
        totalBlocks: result.totalBlocks,
        processedBlocks: result.processedBlocks,
        successfulImports: result.successfulImports,
        failedImports: result.failedImports,
        publishedBlocks: result.publishedBlocks,
        qaReadyBlocks: result.qaReadyBlocks,
        qaCompleteBlocks: result.qaCompleteBlocks,
        publishErrors: result.publishErrors,
      });
    }
    
    return result;
  } catch (error) {
    console.error('Import error:', error);
    if (onProgress) {
      onProgress({
        stage: 'error',
        message: error.message || 'Failed to import blocks',
        error: error.message,
      });
    }
    throw error;
  }
};

